import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import type { SWRConfiguration } from "swr";
import { AppActionCreators } from "@nkr/store/index";
import { ActionCreators as AuthActionCreators } from "@nkr/store/authorization/actions";
import { Fetcher } from "@nkr/utils/rest-api/fetcher";
import type { UserError } from "@nkr/utils/rest-api/fetcher";
import { useAppHookContext } from "@nkr/utils/rest-api/useAppHookContext";

/** Enabled request value for endpoints that do not require request data. */
export const API_EMPTY_REQUEST: unique symbol = Symbol("ApiEmptyRequest");

export type ApiEmptyRequest = typeof API_EMPTY_REQUEST;

export interface ApiResponse<T> {
    isSuccess: boolean;
    message?: string | null;
    errors?: Record<string, string> | null;
    payload?: T | null;
}

export const ApiStatus = {
    NotInitialized: "not-initialized",
    Loading: "loading",
    Loaded: "loaded",
    Error: "error",
} as const;

export type ApiStatus = typeof ApiStatus[keyof typeof ApiStatus];

type ApiQueryControls<T> = {
    refresh: () => Promise<ApiResponse<T> | undefined>;
};

export type ApiQueryResult<T> = ApiQueryControls<T> & (
    | {
        status: typeof ApiStatus.NotInitialized;
        response: undefined;
        error: undefined;
        isRefreshing: false;
    }
    | {
        status: typeof ApiStatus.Loading;
        response: undefined;
        error: undefined;
        isRefreshing: false;
    }
    | {
        status: typeof ApiStatus.Loaded;
        response: ApiResponse<T>;
        error: unknown;
        isRefreshing: boolean;
    }
    | {
        status: typeof ApiStatus.Error;
        response: ApiResponse<T> | undefined;
        error: unknown;
        isRefreshing: false;
    }
);

export interface ApiGetOptions
    extends Omit<SWRConfiguration, "fetcher" | "refreshInterval"> {
    refreshIntervalSeconds?: number;
    loadingMessage?: string;
}

const defaultGetOptions: ApiGetOptions = {
    revalidateOnMount:      true,
    revalidateIfStale:      true,
    revalidateOnFocus:      false,
    revalidateOnReconnect:  true,
    refreshIntervalSeconds: 0,
    dedupingInterval:       5_000,
};

export type ApiExecute<TResponse, TRequest> =
    [TRequest] extends [ApiEmptyRequest]
        ? () => Promise<ApiResponse<TResponse>>
        : (request: TRequest) => Promise<ApiResponse<TResponse>>;

export interface ApiOperation<TResponse, TRequest> {
    execute: ApiExecute<TResponse, TRequest>;
    response: ApiResponse<TResponse> | undefined;
    error: unknown;
    isExecuting: boolean;
    reset: () => void;
}

export interface ApiUrlContext {
    centerId: number | null;
}

export type ApiUrlBuilder<TRequest> = (request: TRequest, context: ApiUrlContext) => string;

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface OperationOptions<TRequest> {
    method: HttpMethod;
    buildUrl: ApiUrlBuilder<TRequest>;
    loadingMessage: string;
    appendRequestToQuery?: boolean;
    sendRequestAsBody?: boolean;
}

function errorResponse<T>(error: unknown): ApiResponse<T> | undefined {
    if (typeof error !== "object" || error === null || !("response" in error)) {
        return undefined;
    }

    return (error as UserError<T>).response as ApiResponse<T> | undefined;
}

function queryValue(value: unknown): string {
    if (value instanceof Date) return value.toISOString();

    switch (typeof value) {
        case "string":
        case "number":
        case "boolean":
        case "bigint":
            return String(value);
        default:
            throw new TypeError("Search values must be primitives, dates, or arrays of those values.");
    }
}

function appendQueryString(url: string, request: unknown): string {
    if (request === API_EMPTY_REQUEST || request === null || request === undefined) {
        return url;
    }

    const entries = typeof request === "object"
        ? Object.entries(request)
        : [["value", request] as const];
    const query = new URLSearchParams();

    for (const [key, value] of entries) {
        if (value === null || value === undefined) continue;

        if (Array.isArray(value)) {
            for (const item of value) {
                if (item !== null && item !== undefined) {
                    query.append(key, queryValue(item));
                }
            }
        } else {
            query.append(key, queryValue(value));
        }
    }

    const serialized = query.toString();
    if (!serialized) return url;

    return `${url}${url.includes("?") ? "&" : "?"}${serialized}`;
}

function requestBody(request: unknown): string | undefined {
    return request === API_EMPTY_REQUEST ? undefined : JSON.stringify(request);
}

const fetcher = new Fetcher();

/**
 * Wraps the shared Fetcher with the bearer token from the Redux auth slice, refreshing it first
 * when it is close to expiring. Mirrors the authed fetchers used by RestApi and LookupApi.
 */
function useAuthedFetch(dispatch: Function) {
    return useMemo(
        () => async <T,>(url: string, init?: RequestInit): Promise<ApiResponse<T>> => {
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
                "Accept": "application/json",
                ...(init?.headers as Record<string, string> | undefined),
            };

            try {
                const auth = await dispatch(AuthActionCreators.checkToken());
                const token = (auth && auth.token && auth.token.access_token) as string | undefined;
                if (token) headers["Authorization"] = `Bearer ${token}`;
            } catch {
                // Token refresh failed; send the request unauthenticated and let the API answer with 401.
            }

            return fetcher.fetch<T>(url, { ...init, headers });
        },
        [dispatch],
    );
}

/** Drives the global Redux spinner for the lifetime of a request. */
function useLoadingIndicator(dispatch: Function, isVisible: boolean, message: string): void {
    const wasVisibleRef = useRef(false);

    useEffect(() => {
        if (isVisible) {
            dispatch(AppActionCreators.showLoadingAsync(message));
            wasVisibleRef.current = true;
            return;
        }

        if (wasVisibleRef.current) {
            dispatch(AppActionCreators.hideLoading());
            wasVisibleRef.current = false;
        }
    }, [dispatch, isVisible, message]);

    useEffect(() => () => {
        if (wasVisibleRef.current) {
            dispatch(AppActionCreators.hideLoading());
            wasVisibleRef.current = false;
        }
    }, [dispatch]);
}

function toRefreshInterval(seconds: number | undefined): number {
    if (seconds === undefined || seconds === 0) return 0;
    if (!Number.isFinite(seconds) || seconds < 0) {
        throw new RangeError("refreshIntervalSeconds must be a finite, nonnegative number.");
    }
    return seconds * 1_000;
}

function useGetOne<TResponse, TRequest = TResponse>(
    request: TRequest | null | undefined,
    buildUrl: ApiUrlBuilder<TRequest>,
    options: ApiGetOptions = {},
): ApiQueryResult<TResponse> {
    const { dispatch, centerId, isReady } = useAppHookContext();
    const authedFetch = useAuthedFetch(dispatch);
    const urlContext = useMemo<ApiUrlContext>(() => ({ centerId }), [centerId]);

    const isEnabled = request !== null && request !== undefined;
    // The query stays keyless until the signed-in user is loaded, so no request goes out unauthenticated.
    const swrKey = isEnabled && isReady ? buildUrl(request as TRequest, urlContext) : null;
    const { refreshIntervalSeconds, loadingMessage, ...swrOptions } = options;
    const refreshInterval = toRefreshInterval(refreshIntervalSeconds);
    const { data, error, isLoading, isValidating, mutate } = useSWR<ApiResponse<TResponse>>(
        swrKey,
        authedFetch,
        { ...defaultGetOptions, ...swrOptions, refreshInterval },
    );
    const failedResponse = errorResponse<TResponse>(error);

    useLoadingIndicator(
        dispatch,
        swrKey !== null && (isLoading || (isValidating && !data && !error)),
        loadingMessage ?? "Loading...",
    );

    useEffect(() => {
        if (swrKey === null || data !== undefined || error !== undefined || isValidating) return;

        // An enabled query must not remain in Loading when SWR has no request in flight.
        void mutate();
    }, [data, error, isValidating, mutate, swrKey]);

    if (!isEnabled) {
        return {
            status: ApiStatus.NotInitialized,
            response: undefined,
            error: undefined,
            isRefreshing: false,
            refresh: mutate,
        };
    }

    if (data?.isSuccess) {
        return {
            status: ApiStatus.Loaded,
            response: data,
            error,
            isRefreshing: isValidating,
            refresh: mutate,
        };
    }

    if (error || failedResponse || (data && !data.isSuccess)) {
        return {
            status: ApiStatus.Error,
            response: failedResponse ?? data,
            error,
            isRefreshing: false,
            refresh: mutate,
        };
    }

    return {
        status: ApiStatus.Loading,
        response: undefined,
        error: undefined,
        isRefreshing: false,
        refresh: mutate,
    };
}

function useGetMany<TResponse, TRequest = TResponse>(
    request: TRequest | null | undefined,
    buildUrl: ApiUrlBuilder<TRequest>,
    options: ApiGetOptions = {},
): ApiQueryResult<TResponse[]> {
    return useGetOne<TResponse[], TRequest>(request, buildUrl, options);
}

function useOperation<TResponse, TRequest>(
    options: OperationOptions<TRequest>,
): ApiOperation<TResponse, TRequest> {
    const { dispatch, centerId } = useAppHookContext();
    const authedFetch = useAuthedFetch(dispatch);
    const [response, setResponse] = useState<ApiResponse<TResponse>>();
    const [error, setError] = useState<unknown>();
    const [isExecuting, setIsExecuting] = useState(false);
    const optionsRef = useRef(options);
    const contextRef = useRef<ApiUrlContext>({ centerId });
    const fetchRef = useRef(authedFetch);
    const dispatchRef = useRef(dispatch);
    const executionRef = useRef(0);
    const mountedRef = useRef(true);
    const spinnerRef = useRef(false);

    useEffect(() => {
        optionsRef.current = options;
        contextRef.current = { centerId };
        fetchRef.current = authedFetch;
        dispatchRef.current = dispatch;
    }, [options, centerId, authedFetch, dispatch]);

    const showSpinner = useCallback((message: string) => {
        spinnerRef.current = true;
        dispatchRef.current(AppActionCreators.showLoadingAsync(message));
    }, []);

    const hideSpinner = useCallback(() => {
        if (!spinnerRef.current) return;
        spinnerRef.current = false;
        dispatchRef.current(AppActionCreators.hideLoading());
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            hideSpinner();
        };
    }, [hideSpinner]);

    const executeInternal = useCallback(async (...args: [TRequest?]) => {
        const execution = ++executionRef.current;
        const request = (args.length === 0 ? API_EMPTY_REQUEST : args[0]) as TRequest;
        const current = optionsRef.current;
        const baseUrl = current.buildUrl(request, contextRef.current);
        const url = current.appendRequestToQuery
            ? appendQueryString(baseUrl, request)
            : baseUrl;
        const init: RequestInit = { method: current.method };

        if (current.sendRequestAsBody) {
            const body = requestBody(request);
            if (body !== undefined) init.body = body;
        }

        if (mountedRef.current) {
            setIsExecuting(true);
            setError(undefined);
        }

        showSpinner(current.loadingMessage);

        try {
            const result = await fetchRef.current<TResponse>(url, init);
            if (mountedRef.current && execution === executionRef.current) {
                setResponse(result);
            }
            return result;
        } catch (caught: unknown) {
            const apiResponse = errorResponse<TResponse>(caught);
            if (mountedRef.current && execution === executionRef.current) {
                setError(caught);
                if (apiResponse) setResponse(apiResponse);
            }
            if (apiResponse) return apiResponse;
            throw caught;
        } finally {
            if (execution === executionRef.current) {
                hideSpinner();
                if (mountedRef.current) setIsExecuting(false);
            }
        }
    }, [showSpinner, hideSpinner]);

    const reset = useCallback(() => {
        ++executionRef.current;
        hideSpinner();
        setResponse(undefined);
        setError(undefined);
        setIsExecuting(false);
    }, [hideSpinner]);

    return {
        execute: executeInternal as ApiExecute<TResponse, TRequest>,
        response,
        error,
        isExecuting,
        reset,
    };
}

export interface ApiClient {
    readonly emptyRequest   : ApiEmptyRequest;
    useGetOne               : typeof useGetOne;
    useGetMany              : typeof useGetMany;
    useGetAction            : <TResponse, TRequest = TResponse>( buildUrl: ApiUrlBuilder<TRequest>, )   => ApiOperation<TResponse, TRequest>;
    useSearch               : <TResponse, TRequest = TResponse>( buildUrl: ApiUrlBuilder<TRequest>, )   => ApiOperation<TResponse[], TRequest>;
    useCreate               : <TEntity>(buildUrl: ApiUrlBuilder<TEntity>)                               => ApiOperation<TEntity, TEntity>;
    useUpdate               : <TEntity>(buildUrl: ApiUrlBuilder<TEntity>)                               => ApiOperation<TEntity, TEntity>;
    useDelete               : <TEntity>(buildUrl: ApiUrlBuilder<TEntity>)                               => ApiOperation<void, TEntity>;
    usePostAction           : <TResponse, TRequest = TResponse>( buildUrl: ApiUrlBuilder<TRequest>, )   => ApiOperation<TResponse, TRequest>;
    usePutAction            : <TResponse, TRequest = TResponse>( buildUrl: ApiUrlBuilder<TRequest>, )   => ApiOperation<TResponse, TRequest>;
}

export const apiClient: ApiClient = {
    emptyRequest    : API_EMPTY_REQUEST,
    useGetOne,
    useGetMany,
    useGetAction    : <TResponse, TRequest = TResponse>(buildUrl: ApiUrlBuilder<TRequest>)  => useOperation<TResponse, TRequest>({ method: "GET", buildUrl, loadingMessage: "Loading..." }),
    useSearch       : <TResponse, TRequest = TResponse>(buildUrl: ApiUrlBuilder<TRequest>)  => useOperation<TResponse[], TRequest>({ method: "GET", buildUrl, loadingMessage: "Searching...", appendRequestToQuery: true,}),
    useCreate       : <TEntity>(buildUrl: ApiUrlBuilder<TEntity>)                           => useOperation<TEntity, TEntity>({ method: "POST", buildUrl, loadingMessage: "Saving...", sendRequestAsBody: true }),
    useUpdate       : <TEntity>(buildUrl: ApiUrlBuilder<TEntity>)                           => useOperation<TEntity, TEntity>({ method: "PUT", buildUrl, loadingMessage: "Saving...", sendRequestAsBody: true }),
    useDelete       : <TEntity>(buildUrl: ApiUrlBuilder<TEntity>)                           => useOperation<void, TEntity>({ method: "DELETE", buildUrl, loadingMessage: "Deleting...", sendRequestAsBody: true }),
    usePostAction   : <TResponse, TRequest = TResponse>(buildUrl: ApiUrlBuilder<TRequest>)  => useOperation<TResponse, TRequest>({ method: "POST", buildUrl, loadingMessage: "Saving...", sendRequestAsBody: true }),
    usePutAction    : <TResponse, TRequest = TResponse>(buildUrl: ApiUrlBuilder<TRequest>)  => useOperation<TResponse, TRequest>({ method: "PUT", buildUrl, loadingMessage: "Saving...", sendRequestAsBody: true }),
};
